import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import css from './LandingPage.module.css';

// Static assets
import facebookImage from '../../assets/tradmarket-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/tradmarket-twitter-sharing-600x315.jpg';

// Section pages
import PopularInstruments from './Sections/PopularInstruments';
import SectionHero from './SectionHero/SectionHero';
import SectionAboutUs from './Sections/SectionAboutUs';
import ExploreSection from './Sections/ExploreSection';
import SectionHowItWorks from './Sections/SectionHowItWorks';
import SectionTrust from './Sections/SectionTrust';
import SectionSell from './Sections/SectionSell';
import { createResourceLocatorString } from '../../util/routes';

export const LandingPageComponent = props => {
  const { intl, scrollingDisabled, history, listings } = props;

  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const marketplaceName = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'LandingPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({
    id: 'LandingPage.schemaDescription',
  });
  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;

  // The hero and the trust band fill the first screen when they fit: the screen less what
  // sits above them (the notice, if shown, and the topbar), kept in step as that changes
  const firstScreenRef = useRef(null);
  useEffect(() => {
    const el = firstScreenRef.current;
    if (!el || typeof window === 'undefined') return undefined;
    const measure = () => {
      const above = Math.max(0, el.getBoundingClientRect().top + window.scrollY);
      el.style.setProperty('--aboveFirstScreen', `${Math.round(above)}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    if (observer) observer.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      if (observer) observer.disconnect();
    };
  }, []);

  // The hero's search: the market, with the words typed (all of it when none)
  const onSearch = keywords =>
    history.push(
      createResourceLocatorString(
        'SearchPage',
        routeConfiguration,
        {},
        keywords ? { keywords } : {}
      )
    );

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={schemaTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: twitterImage,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <div ref={firstScreenRef} className={css.firstScreen}>
            <SectionHero intl={intl} onSearch={onSearch} />
            <SectionTrust intl={intl} />
          </div>
          <PopularInstruments intl={intl} />
          <ExploreSection intl={intl} listings={listings} />
          <SectionHowItWorks intl={intl} />
          <SectionSell intl={intl} />
          <SectionAboutUs intl={intl} />
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

LandingPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  intl: intlShape.isRequired,
};

LandingPageComponent.defaultProps = {};

const mapStateToProps = state => {
  const { currentPageResultIds } = state.LandingPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    listings,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({});

const LandingPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withViewport,
  injectIntl
)(LandingPageComponent);

export default LandingPage;
